import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react";
import classNames from "classnames";
import { BtnUi } from "../BtnUi";
import DateRangePicker from "../DateRangePicker/DateRangePicker";
import { today, getLocalTimeZone } from "@internationalized/date";
import { buildMarriottSearchUrl } from "../../utils/buildMarriottSearchUrl";
import { useSuggestedPlaces } from "../../hooks/useSuggestedPlaces";
import { getAnalyticsClickTrackingAttributes } from "~/utils/adobeDataLayerUtils";
import styles from "./DestinationSearch.module.css";
import DestinationIcon from "../../assets/offers/destination.svg";
import DateIcon from "../../assets/offers/date.svg";
import PeopleIcon from "../../assets/offers/people.svg";
import { ArrowUpRight } from "../Icon/Icons/PassionsHub/ArrowUpRight";
import { Star } from "../Icon/Icons/PassionsHub/Star";

export const DestinationSearch = ({
  className,
  onExploreStays,
  initialDestination,
  heroImage = "/web-src/src/assets/passions/destination_search.png",
  placeId,
  // Recently viewed property data (from usePassionsHubHotels)
  recentlyViewedProperty = null,
  loading = false,
  ...props
}) => {
  // Use empty string as fallback when no destination provided
  const effectiveDestination = initialDestination || "";
  const effectivePlaceId = placeId || "";

  const [destination, setDestination] = useState(effectiveDestination);
  const [searchQuery, setSearchQuery] = useState(effectiveDestination);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedPlaceId, setSelectedPlaceId] = useState(effectivePlaceId);

  // Date range state
  const [dateRange, setDateRange] = useState({
    start: today(getLocalTimeZone()).add({
      days: (5 - today(getLocalTimeZone()).day + 7) % 7, // Next Friday
    }),
    end: today(getLocalTimeZone()).add({
      days: ((5 - today(getLocalTimeZone()).day + 7) % 7) + 2, // Next Sunday (Friday + 2 days)
    }),
  });
  const [isDatepickerOpened, setIsDatepickerOpened] = useState(false);

  // Rooms and guests state
  const [rooms, setRooms] = useState(1);
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState([]);
  const [showGuestPicker, setShowGuestPicker] = useState(false);

  const guestPickerRef = useRef(null);
  const suggestionsRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Use suggested places hook
  const {
    suggestedPlaces,
    loading: placesLoading,
    error: placesError,
    fetchSuggestedPlaces,
    fetchPlaceDetails,
    formatPlaceName,
    getPlaceId,
  } = useSuggestedPlaces(searchQuery);

  // Recently viewed property helper functions
  const getRecentPropertyImageUrl = useCallback(() => {
    const url = recentlyViewedProperty?.imageUrl;
    if (!url) return null;
    if (url.startsWith("http")) return url;
    // Remove leading slash if present to avoid double slashes
    const cleanUrl = url.startsWith("/") ? url.substring(1) : url;
    return `https://cache.marriott.com/${cleanUrl}`;
  }, [recentlyViewedProperty]);

  const getRecentPropertyName = useCallback(() => {
    return recentlyViewedProperty?.name || null;
  }, [recentlyViewedProperty]);

  const getRecentPropertyRating = useCallback(() => {
    return recentlyViewedProperty?.reviews?.rating || null;
  }, [recentlyViewedProperty]);

  const getRecentPropertyStars = useCallback(() => {
    const stars = recentlyViewedProperty?.reviews?.stars;
    if (!stars) return null;
    // Ensure we have a valid positive integer for the Array constructor to avoid RangeError
    const numStars = Math.floor(Number(stars));
    return numStars > 0 ? numStars : null;
  }, [recentlyViewedProperty]);

  const getRecentPropertyReviewCount = useCallback(() => {
    const count = recentlyViewedProperty?.reviews?.count;
    if (!count) return null;
    return count.toLocaleString();
  }, [recentlyViewedProperty]);

  const getRecentPropertyUrl = useCallback(() => {
    return recentlyViewedProperty?.navigationLink || null;
  }, [recentlyViewedProperty]);

  const getRecentPropertyDestination = useCallback(() => {
    return recentlyViewedProperty?.name || null;
  }, [recentlyViewedProperty]);

  // Determine if we should show the "Pick up where you left off" mode
  const showRecentlyViewedMode = Boolean(recentlyViewedProperty);

  // Determine if we have a destination set (hide input when destination is visible)
  const hasDestination = Boolean(initialDestination);

  // Hide destination input when we have a destination or in recently viewed mode
  const hideDestinationInput = hasDestination || showRecentlyViewedMode;

  // Calculate total guests
  const totalGuests = adults + children.length;

  // Format rooms and guests text
  const roomsGuestsText = useMemo(() => {
    const roomText = rooms === 1 ? "1 Room" : `${rooms} Rooms`;
    const guestText = totalGuests === 1 ? "1 Guest" : `${totalGuests} Guests`;
    return `${roomText}, ${guestText}`;
  }, [rooms, totalGuests]);

  // Handle date range change
  const handleDateRangeChange = useCallback((range) => {
    setDateRange(range);
  }, []);

  // Toggle guest picker
  const toggleGuestPicker = useCallback(() => {
    setShowGuestPicker((prev) => !prev);
  }, []);

  // Close guest picker when clicking outside
  const closeGuestPicker = useCallback(
    (event) => {
      if (
        showGuestPicker &&
        guestPickerRef.current &&
        !guestPickerRef.current.contains(event.target) &&
        !isDatepickerOpened
      ) {
        setShowGuestPicker(false);
      }
    },
    [showGuestPicker, isDatepickerOpened],
  );

  // Handle escape key for guest picker
  const handleEscapeKey = useCallback(
    (event) => {
      if (event.key === "Escape") {
        if (showGuestPicker) {
          setShowGuestPicker(false);
        }
        if (showSuggestions) {
          setShowSuggestions(false);
        }
      }
    },
    [showGuestPicker, showSuggestions],
  );

  // Close suggestions dropdown when clicking outside
  const closeSuggestions = useCallback(
    (event) => {
      if (
        showSuggestions &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    },
    [showSuggestions],
  );

  // Debounced search function for places
  const debouncedSearch = useCallback(
    (value) => {
      if (value.length >= 2) {
        fetchSuggestedPlaces(value);
        setShowSuggestions(true);
      } else {
        setShowSuggestions(false);
      }
    },
    [fetchSuggestedPlaces],
  );

  // Handle destination input change
  const handleDestinationChange = (e) => {
    const value = e.target.value;
    setDestination(value);
    setSearchQuery(value);

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer for debounced search
    debounceTimerRef.current = setTimeout(() => {
      debouncedSearch(value);
    }, 300); // 300ms delay
  };

  // Handle suggestion selection
  const handleSuggestionSelect = (place) => {
    const currentPlaceId = getPlaceId(place);
    const formattedName = formatPlaceName(place);
    setDestination(formattedName);
    setSearchQuery(formattedName);
    setSelectedPlaceId(currentPlaceId);
    setShowSuggestions(false);

    // Fetch place details for the selected place
    fetchPlaceDetails(currentPlaceId);
  };

  // Add event listeners
  useEffect(() => {
    document.addEventListener("click", closeGuestPicker);
    document.addEventListener("click", closeSuggestions);
    document.addEventListener("keydown", handleEscapeKey);

    return () => {
      document.removeEventListener("click", closeGuestPicker);
      document.removeEventListener("click", closeSuggestions);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [closeGuestPicker, closeSuggestions, handleEscapeKey]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Sync destination state when initialDestination prop changes (after loading completes)
  useEffect(() => {
    if (initialDestination && initialDestination !== destination) {
      setDestination(initialDestination);
      setSearchQuery(initialDestination);
    }
  }, [initialDestination]);

  // Sync placeId when prop changes
  useEffect(() => {
    if (placeId && placeId !== selectedPlaceId) {
      setSelectedPlaceId(placeId);
    }
  }, [placeId]);

  const handleExploreStays = () => {
    // In recently viewed mode, navigate directly to the property page
    if (showRecentlyViewedMode) {
      const propertyUrl = getRecentPropertyUrl();
      if (propertyUrl) {
        window.open(propertyUrl, "_blank");

        // Still call the callback if provided
        if (onExploreStays) {
          onExploreStays({
            destination: getRecentPropertyName(),
            dateRange,
            rooms,
            adults,
            children,
            totalGuests,
            propertyId: recentlyViewedProperty?.id,
            propertyUrl,
          });
        }
        return;
      }
    }

    // Use the selected place ID or fall back to the prop
    const currentPlaceId = selectedPlaceId || placeId;

    // Build and open Marriott search URL
    const marriottUrl = buildMarriottSearchUrl({
      destination,
      dateRange,
      rooms,
      adults,
      children,
      placeId: currentPlaceId,
    });
    window.open(marriottUrl, "_blank");

    // Still call the callback if provided for additional handling
    if (onExploreStays) {
      onExploreStays({
        destination,
        dateRange,
        rooms,
        adults,
        children,
        totalGuests,
        placeId: currentPlaceId,
      });
    }
  };

  // Render loading skeleton
  if (loading) {
    return (
      <div
        className={classNames(styles.container, styles.loading, className)}
        {...props}
      >
        <div className={styles.header}>
          <div className={styles.titleContainer}>
            <div
              className={classNames(styles.titleSkeleton, styles.skeleton)}
            />
          </div>
          <div className={styles.heroImageContainer}>
            <div
              className={classNames(styles.heroImageSkeleton, styles.skeleton)}
            />
          </div>
        </div>
        <div className={styles.searchForm}>
          <div className={styles.formContainer}>
            <div
              className={classNames(styles.fieldSkeleton, styles.skeleton)}
            />
            <div
              className={classNames(styles.fieldSkeleton, styles.skeleton)}
            />
            <div
              className={classNames(styles.fieldSkeleton, styles.skeleton)}
            />
          </div>
          <div className={classNames(styles.buttonSkeleton, styles.skeleton)} />
        </div>
      </div>
    );
  }

  return (
    <div className={classNames(styles.container, className)} {...props}>
      {/* Header Section - conditionally shows recently viewed or default */}
      <div className={styles.header}>
        <div className={styles.titleContainer}>
          <h2 className={styles.title}>
            {showRecentlyViewedMode
              ? "Pick up where you left off — Your next stay is a tap away."
              : "Discover your next destination and unlock Silver Elite"}
          </h2>
        </div>
        <div className={styles.heroImageContainer}>
          <img
            src={
              showRecentlyViewedMode ? getRecentPropertyImageUrl() : heroImage
            }
            alt={
              showRecentlyViewedMode ? getRecentPropertyName() : "Destination"
            }
            className={styles.heroImage}
          />
        </div>
        {/* Recently viewed hotel info - shown only in recently viewed mode */}
        {showRecentlyViewedMode && (
          <div className={styles.recentHotelInfo}>
            <h3 className={styles.recentHotelName}>
              {getRecentPropertyDestination() || getRecentPropertyName()}
            </h3>
            <div className={styles.recentHotelRating}>
              {getRecentPropertyStars() && (
                <>
                  {[...Array(getRecentPropertyStars())].map((_, i) => (
                    <Star key={i} className={styles.starIcon} />
                  ))}
                </>
              )}
              {getRecentPropertyRating() && (
                <span className={styles.ratingValue}>
                  {getRecentPropertyRating()}
                </span>
              )}
              {getRecentPropertyReviewCount() && (
                <span className={styles.reviewCount}>
                  ({getRecentPropertyReviewCount()} Reviews)
                </span>
              )}
            </div>
          </div>
        )}
        {/* Destination info - shown when destination is set but not in recently viewed mode */}
        {hasDestination && !showRecentlyViewedMode && (
          <div className={styles.recentHotelInfo}>
            <h3 className={styles.recentHotelName}>{initialDestination}</h3>
          </div>
        )}
      </div>

      {/* Search Form Section */}
      <div className={styles.searchForm}>
        <div className={styles.formContainer}>
          {/* Destination Field - hidden when destination is set or in recently viewed mode */}
          {!hideDestinationInput && (
            <div className={styles.fieldGroup} ref={suggestionsRef}>
              <div className={styles.fieldLabel}>
                <div className={styles.iconWrapper}>
                  <img
                    src={DestinationIcon}
                    alt="Destination"
                    width="16"
                    height="16"
                  />
                </div>
                <span className={styles.labelText}>DESTINATION</span>
              </div>
              <div className={styles.fieldInput}>
                <input
                  data-testid="destination-search-input"
                  aria-label="Destination"
                  type="text"
                  value={destination}
                  onChange={handleDestinationChange}
                  onFocus={() =>
                    destination.length >= 2 && setShowSuggestions(true)
                  }
                  className={styles.input}
                  placeholder="Where can we take you?"
                />
                {showSuggestions && (
                  <div className={styles.suggestionsDropdown}>
                    {placesLoading ? (
                      <div
                        className={classNames(
                          styles.suggestionItem,
                          styles.loading,
                        )}
                      >
                        Loading suggestions...
                      </div>
                    ) : placesError ? (
                      <div
                        className={classNames(
                          styles.suggestionItem,
                          styles.error,
                        )}
                      >
                        Error loading suggestions
                      </div>
                    ) : suggestedPlaces.length > 0 ? (
                      suggestedPlaces.map((place, index) => (
                        <div
                          key={`suggestion-${index}`}
                          data-testid="destination-suggestion"
                          className={styles.suggestionItem}
                          onClick={() => handleSuggestionSelect(place)}
                        >
                          {formatPlaceName(place)}
                        </div>
                      ))
                    ) : (
                      <div
                        className={classNames(
                          styles.suggestionItem,
                          styles.noResults,
                        )}
                      >
                        No places found
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Stay Dates Field */}
          <div className={styles.fieldGroup}>
            <div className={styles.fieldLabel}>
              <div className={styles.iconWrapper}>
                <img src={DateIcon} alt="Stay Dates" width="16" height="16" />
              </div>
              <span className={styles.labelText}>STAY DATES</span>
            </div>
            <div className={styles.fieldInput}>
              <DateRangePicker
                value={dateRange}
                handleDateRangeChange={handleDateRangeChange}
                minValue={today(getLocalTimeZone())}
                onOpenedChange={setIsDatepickerOpened}
              />
            </div>
          </div>

          {/* Rooms & Guests Field */}
          <div
            className={classNames(styles.fieldGroup, styles.guestPickerGroup)}
            ref={guestPickerRef}
          >
            <div className={styles.fieldLabel}>
              <div className={styles.iconWrapper}>
                <img
                  src={PeopleIcon}
                  alt="Rooms & Guests"
                  width="16"
                  height="16"
                />
              </div>
              <span className={styles.labelText}>ROOMS & GUESTS</span>
            </div>
            <div className={styles.fieldInput}>
              <button
                data-testid="destination-guests-trigger"
                type="button"
                onClick={toggleGuestPicker}
                className={styles.guestPickerButton}
              >
                {roomsGuestsText}
              </button>
              {showGuestPicker && (
                <GuestPickerComponent
                  rooms={rooms}
                  adults={adults}
                  childrenArray={children}
                  setRooms={setRooms}
                  setAdults={setAdults}
                  setChildren={setChildren}
                />
              )}
            </div>
          </div>

          {/* Explore Button */}
          <BtnUi
            data-testid="destination-explore-stays"
            variant="primary"
            className={classNames(styles.exploreButton, "custom_click_track")}
            onClick={handleExploreStays}
            {...getAnalyticsClickTrackingAttributes(
              "Destination Search",
              "click",
              showRecentlyViewedMode ? "Resume Stays" : "Explore Stays",
            )}
          >
            <span>Explore stays</span>
            <ArrowUpRight className={styles.buttonIcon} />
          </BtnUi>
        </div>
      </div>
    </div>
  );
};

// Guest Picker Component
const GuestPickerComponent = ({
  rooms,
  adults,
  childrenArray,
  setRooms,
  setAdults,
  setChildren,
}) => {
  const totalGuests = adults + childrenArray.length;

  const updateRooms = (increment) => {
    const newRooms = increment ? rooms + 1 : rooms - 1;
    if (newRooms >= 1 && newRooms <= 6) {
      setRooms(newRooms);
    }
  };

  const updateAdults = (increment) => {
    const newAdults = increment ? adults + 1 : adults - 1;
    if (newAdults >= 1 && newAdults + childrenArray.length <= 8) {
      setAdults(newAdults);
    }
  };

  const updateChildren = (increment) => {
    if (increment && totalGuests < 8) {
      setChildren([...childrenArray, 0]); // Add child with age 0
    } else if (!increment && childrenArray.length > 0) {
      setChildren(childrenArray.slice(0, -1)); // Remove last child
    }
  };

  const updateChildAge = (index, age) => {
    const newChildren = [...childrenArray];
    newChildren[index] = Math.max(0, Math.min(17, age));
    setChildren(newChildren);
  };

  return (
    <div className={styles.guestPicker}>
      {/* Rooms */}
      <div className={styles.guestPickerRow}>
        <div className={styles.guestPickerLabel}>
          <span>Rooms</span>
        </div>
        <div className={styles.guestPickerControls}>
          <button
            type="button"
            onClick={() => updateRooms(false)}
            disabled={rooms <= 1}
            className={styles.guestPickerButton}
          >
            -
          </button>
          <span className={styles.guestPickerValue}>{rooms}</span>
          <button
            type="button"
            onClick={() => updateRooms(true)}
            disabled={rooms >= 6}
            className={styles.guestPickerButton}
          >
            +
          </button>
        </div>
      </div>

      {/* Adults */}
      <div className={styles.guestPickerRow}>
        <div className={styles.guestPickerLabel}>
          <span>Adults</span>
          <small>(Max: 8 total guests/room)</small>
        </div>
        <div className={styles.guestPickerControls}>
          <button
            type="button"
            onClick={() => updateAdults(false)}
            disabled={adults <= 1}
            className={styles.guestPickerButton}
          >
            -
          </button>
          <span className={styles.guestPickerValue}>{adults}</span>
          <button
            type="button"
            onClick={() => updateAdults(true)}
            disabled={totalGuests >= 8}
            className={styles.guestPickerButton}
          >
            +
          </button>
        </div>
      </div>

      {/* Children */}
      <div className={styles.guestPickerRow}>
        <div className={styles.guestPickerLabel}>
          <span>Children</span>
          <small>
            (Max: 8 total guests/room) Age-based rates may be available
          </small>
        </div>
        <div className={styles.guestPickerControls}>
          <button
            type="button"
            onClick={() => updateChildren(false)}
            disabled={childrenArray.length === 0}
            className={styles.guestPickerButton}
          >
            -
          </button>
          <span className={styles.guestPickerValue}>
            {childrenArray.length}
          </span>
          <button
            type="button"
            onClick={() => updateChildren(true)}
            disabled={totalGuests >= 8}
            className={styles.guestPickerButton}
          >
            +
          </button>
        </div>
      </div>

      {/* Child Ages */}
      {childrenArray.map((age, index) => (
        <div key={index} className={styles.guestPickerRow}>
          <div className={styles.guestPickerLabel}>
            <span>Child {index + 1}: Age</span>
          </div>
          <div className={styles.guestPickerControls}>
            <button
              type="button"
              onClick={() => updateChildAge(index, age - 1)}
              disabled={age <= 0}
              className={styles.guestPickerButton}
            >
              -
            </button>
            <span className={styles.guestPickerValue}>{age}</span>
            <button
              type="button"
              onClick={() => updateChildAge(index, age + 1)}
              disabled={age >= 17}
              className={styles.guestPickerButton}
            >
              +
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};
